import { PortfolioAiGroup, PortfolioAiVersion, PortfolioEntry, visibleAiCreations } from './types';

export function optionLabel(index: number): string {
  let n = index + 1, label = '';
  while (n > 0) { n--; label = String.fromCharCode(65 + n % 26) + label; n = Math.floor(n / 26); }
  return label;
}

export function groupOptions(entry: Pick<PortfolioEntry, 'aiVersions'>, groupId: string): PortfolioAiVersion[] {
  return visibleAiCreations(entry.aiVersions).filter(ai => ai.groupId === groupId)
    .sort((a, b) => (a.optionOrder ?? a.createdAt) - (b.optionOrder ?? b.createdAt) || a.id.localeCompare(b.id));
}

export function orderedAiGroups(entry: Pick<PortfolioEntry, 'aiGroups'>): PortfolioAiGroup[] {
  return [...(entry.aiGroups || [])].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

export function groupIsRevealed(options: PortfolioAiVersion[]): boolean {
  return options.length > 0 && options.every(ai => ai.status === 'revealed');
}

export function groupNeedsReview(group: PortfolioAiGroup, options: PortfolioAiVersion[]): boolean {
  return !group.reviewed || !groupIsRevealed(options) || (options.length > 1 && !options.some(ai => ai.id === group.favoriteId));
}

/** Filename hints are suggestions, never authoritative assignments. */
export function suggestedShot(filename: string): number | null {
  const match = filename.match(/(?:^|[\s_-])shot[\s_-]*(\d+)/i);
  const value = match ? Number(match[1]) : 0;
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

export interface GroupAssignment { id: string; groupId: string | null; optionOrder: number }

/** Preserve review data while editing titles/order; invalidate changed membership. */
export function applyGroupConfiguration(
  entry: PortfolioEntry,
  groups: PortfolioAiGroup[],
  assignments: GroupAssignment[],
): Pick<PortfolioEntry, 'aiGroups' | 'aiVersions'> {
  const ids = new Set(groups.map(g => g.id));
  if (ids.size !== groups.length || groups.some(g => !g.id || !g.title.trim())) throw new Error('Give each group a name.');
  if (assignments.some(a => a.groupId && !ids.has(a.groupId))) throw new Error('Choose an existing group for each option.');
  const edits = new Map(assignments.map(a => [a.id, a]));
  const aiVersions = entry.aiVersions.map(ai => {
    const edit = edits.get(ai.id);
    if (!edit) return ai;
    const next = { ...ai };
    if (edit.groupId) { next.groupId = edit.groupId; next.optionOrder = edit.optionOrder; }
    else { delete next.groupId; delete next.optionOrder; }
    return next;
  });
  // A queued upload may have joined a group since the editor opened.
  if (aiVersions.some(ai => ai.groupId && !ids.has(ai.groupId))) throw new Error('This group has new uploads. Reopen the editor before removing it.');
  const aiGroups = groups.map((g, order) => {
    const old = entry.aiGroups?.find(item => item.id === g.id);
    const before = groupOptions(entry, g.id).map(ai => ai.id).sort().join(',');
    const options = groupOptions({ aiVersions }, g.id);
    const changed = before !== options.map(ai => ai.id).sort().join(',');
    const favoriteId = options.some(ai => ai.id === old?.favoriteId) ? old!.favoriteId : null;
    return { ...old, id: g.id, title: g.title.trim().slice(0, 200), kind: g.kind, order,
      favoriteId, reviewed: changed ? false : !!old?.reviewed };
  });
  return { aiGroups, aiVersions };
}

/** Archived uploads stay in the editor/restore tray, but disappear from the class board. */
export function activeAiGroups(entry: Pick<PortfolioEntry, 'aiGroups' | 'aiVersions'>): PortfolioAiGroup[] {
  return orderedAiGroups(entry).filter(group => {
    const uploads = entry.aiVersions.filter(option => option.groupId === group.id);
    return !uploads.length || uploads.some(option => !option.archived);
  });
}
